import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
// AssignmentOutlinedIcon import removed with the hidden Onboarding tab; re-add if restoring it.
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { useAppSelector } from "../../../store/types";
import { transformProfile } from "../../transformers/profileTransformer";
import { useProfileModal } from "./ProfileModalContext";
import ProfileReviewNav from "./ProfileReviewNav";

const BORDER = "rgba(17, 24, 39, 0.08)";
// Row 5: collapse the profile nav rail so the profile fits a ~15" monitor
// without horizontal scroll. Persisted per-device so the choice sticks.
const COLLAPSE_KEY = "profile_sidebar_collapsed";

/**
 * The row every profile tab lays out as [ProfileSidebar | content]. Below `md`
 * (phones, where TalentLayout's sidebar is a drawer) it stacks: the sidebar
 * becomes a tab strip above a full-width content column.
 */
export const profileTabRowSx = {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "stretch", md: "flex-start" },
    gap: 2,
    minWidth: 0,
} as const;

export default function ProfileSidebar() {
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const path = location.pathname;
    const modal = useProfileModal();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const [collapsed, setCollapsed] = useState<boolean>(() => {
        try { return localStorage.getItem(COLLAPSE_KEY) === "true"; } catch { return false; }
    });
    const toggleCollapsed = () => {
        setCollapsed((c) => {
            const next = !c;
            try { localStorage.setItem(COLLAPSE_KEY, String(next)); } catch { /* ignore */ }
            return next;
        });
    };

    const rawProfile = useAppSelector((s) => s.profile?.data);
    const profile = useMemo(() => (rawProfile ? transformProfile(rawProfile) : null), [rawProfile]);

    useEffect(() => {
        if (profile?.first_name && profile?.last_name) {
            document.title = `${profile.first_name} ${profile.last_name} | Talent Tooling`;
        }
        return () => { document.title = "Talent Tooling"; };
    }, [profile?.first_name, profile?.last_name]);

    // The ProfileModal renders these sections with a horizontal top nav instead of this
    // left sidebar, so suppress it there.
    if (!id || modal?.inModal) {
        return null;
    }

    const basePath = `/talent/profile/${id}`;

    const navItems: { to: string; label: string; icon: React.ReactNode }[] = [
        { to: basePath, label: "Profile", icon: <PersonOutlineIcon fontSize="small" /> },
        { to: `${basePath}/platform-profile`, label: "Platform Profile", icon: <AccountBoxOutlinedIcon fontSize="small" /> },
        { to: `${basePath}/interviews`, label: "Interviews", icon: <RecordVoiceOverOutlinedIcon fontSize="small" /> },
        { to: `${basePath}/timeline`, label: "Timeline", icon: <TimelineOutlinedIcon fontSize="small" /> },
        // Onboarding tab hidden 2026-07-17 (no longer used). Route/page left intact for easy restore.
        { to: `${basePath}/opportunities`, label: "Opportunities", icon: <AutoAwesomeOutlinedIcon fontSize="small" /> },
        { to: `${basePath}/external-links`, label: "External Links", icon: <LinkOutlinedIcon fontSize="small" /> },
    ];

    if (mobile) {
        // Phones: the same links as a scrollable tab strip above the page, under
        // the review Prev / Next when the profile was opened from a list.
        return (
            <Paper sx={{ flexShrink: 0, border: `1px solid ${BORDER}`, overflow: "hidden" }}>
                <ProfileReviewNav profileId={id} />
                <Tabs
                    value={navItems.some((item) => item.to === path) ? path : false}
                    variant="scrollable"
                    scrollButtons={false}
                    aria-label="Profile sections"
                    sx={{ minHeight: 44, "& .MuiTab-root": { minHeight: 44, py: 0, px: 1.5, fontSize: 13, textTransform: "none" } }}
                >
                    {navItems.map((item) => (
                        <Tab
                            key={item.to}
                            value={item.to}
                            label={item.label}
                            icon={item.icon as React.ReactElement}
                            iconPosition="start"
                            component={RouterLink}
                            to={item.to}
                        />
                    ))}
                </Tabs>
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                width: collapsed ? 48 : 220,
                flexShrink: 0,
                border: `1px solid ${BORDER}`,
                height: "fit-content",
                transition: "width 0.2s ease",
                overflow: "hidden",
            }}
        >
            {/* Sequential-review Prev/Next — only renders when this profile was opened
                from a talent list page (see utils/reviewNav.ts). Hidden while collapsed. */}
            {!collapsed && <ProfileReviewNav profileId={id} />}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "flex-end",
                    px: collapsed ? 0 : 1,
                    pt: 1,
                }}
            >
                <Tooltip title={collapsed ? "Expand panel" : "Collapse panel"} placement="right">
                    <IconButton size="small" onClick={toggleCollapsed}>
                        {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Box>
            <Divider />
            <List dense sx={{ p: collapsed ? 0.75 : 1.5, display: "grid", gap: 0.5 }}>
                {navItems.map((item) => {
                    const btn = (
                        <ListItemButton
                            component={RouterLink}
                            to={item.to}
                            selected={path === item.to}
                            sx={{
                                borderRadius: 1.5,
                                justifyContent: collapsed ? "center" : "flex-start",
                                px: collapsed ? 1 : 2,
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, justifyContent: "center" }}>
                                {item.icon}
                            </ListItemIcon>
                            {!collapsed && (
                                <ListItemText
                                    primary={<Typography sx={{ fontSize: 13 }}>{item.label}</Typography>}
                                />
                            )}
                        </ListItemButton>
                    );
                    return collapsed ? (
                        <Tooltip key={item.to} title={item.label} placement="right">
                            {btn}
                        </Tooltip>
                    ) : (
                        <Box key={item.to}>{btn}</Box>
                    );
                })}
            </List>
        </Paper>
    );
}
